import { Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import {
  DirectShipType,
  ISupplierPrimaryContact,
  ISupplierPurchasingAttribute,
} from 'contacts/types';
import { InfoCard } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Email, PhoneNumber } from 'core/elements';
import { NumberHelper } from 'core/helpers';
import { IInfoCardLine } from 'core/types';
import { getPercentage } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { Level } from 'theme/enums';

interface IProps {
  primaryContact: ISupplierPrimaryContact | null;
  purchasingAttributes: (ISupplierPurchasingAttribute | null)[];
}

export const ExternalSupplierInfo = ({ primaryContact, purchasingAttributes }: IProps) => {
  const { t } = useTranslation();

  const filteredPurchasingAttributes =
    purchasingAttributes?.filter((attribute) => attribute && Object.keys(attribute).length > 0) ||
    [];

  const primaryContactItems: IInfoCardLine[] = [
    {
      label: t('labelVendorPrimaryContactName'),
      value: primaryContact?.contactName,
    },
    {
      label: t('labelVendorPrimaryContactEmail'),
      value: <Email email={primaryContact?.email} />,
      hidden: primaryContact?.email == null,
      sx: { overflowWrap: 'anywhere' },
    },
    {
      label: t('labelVendorPrimaryContactPhone'),
      value: <PhoneNumber phone={primaryContact?.phonenumber} />,
      hidden: primaryContact?.phonenumber == null,
    },
    {
      label: t('labelVendorPrimaryContactShippingLocation'),
      value: primaryContact?.shippingLocation,
    },
  ];

  const getPurchasingAttributeItems = (
    attribute: ISupplierPurchasingAttribute
  ): IInfoCardLine[] => {
    const isDirectShipPercentage = attribute?.directShip === DirectShipType.PERCENTAGE;
    const directShipFeeValue = isDirectShipPercentage
      ? getPercentage(attribute?.directShipFee, { isRawPercentage: true })
      : PricingHelper.formatPricing({ amount: attribute?.directShipFee });

    return [
      {
        label: t('labelVendorPurchasingAttributeNotes'),
        value: attribute?.notes,
      },
      {
        label: t('labelVendorPurchasingAttributeMinOrderAmount'),
        value: PricingHelper.formatPricing({ amount: attribute?.minOrderAmount }),
        hidden: attribute?.minOrderAmount == null,
      },
      {
        label: t('labelVendorPurchasingAttributeMinOrderQuantity'),
        value: NumberHelper.format({ amount: attribute?.minOrderQuantity }),
        hidden: attribute?.minOrderQuantity == null,
      },
      {
        label: t('labelVendorPurchasingAttributeMinOrderWeight'),
        value: NumberHelper.format({ amount: attribute?.minOrderWeight }),
        hidden: attribute?.minOrderWeight == null,
      },
      {
        label: t('labelVendorPurchasingAttributeMinOrderFee'),
        value: PricingHelper.formatPricing({ amount: attribute?.minOrderFee }),
        hidden: attribute?.minOrderFee == null,
      },
      {
        label: t('labelVendorPurchasingAttributeMinTransactionAmount'),
        value: PricingHelper.formatPricing({ amount: attribute?.minTransactionAmount }),
        hidden: attribute?.minTransactionAmount == null,
      },
      {
        label: t('labelVendorPurchasingAttributeMinTransactionWeight'),
        value: NumberHelper.format({ amount: attribute?.minTransactionWeight }),
        hidden: attribute?.minTransactionWeight == null,
      },
      {
        label: t('labelVendorPurchasingAttributeFreightTerms'),
        value: attribute?.freightTerms,
      },
      {
        label: t('labelVendorPurchasingAttributeShipMethod'),
        value: attribute?.shipMethod,
      },
      {
        label: t('labelVendorPurchasingAttributeDirectShip'),
        value: attribute?.directShip,
      },
      {
        label: t('labelVendorPurchasingAttributeDirectShipFee'),
        value: directShipFeeValue,
        hidden: attribute?.directShipFee == null,
      },
      {
        label: t('labelVendorPurchasingAttributeStockBuyer'),
        value: attribute?.stockBuyer,
      },
      {
        label: t('labelVendorPurchasingAttributeNonStockBuyer'),
        value: attribute?.nonStockBuyer,
      },
      {
        label: t('labelVendorPurchasingAttributeDSBuyer'),
        value: attribute?.dsBuyer,
      },
      {
        label: t('labelVendorPurchasingAttributeDSFreightTerms'),
        value: attribute?.dsFreightTerms,
      },
    ];
  };

  return (
    <Stack gap={0.5}>
      <InfoCard
        title={t('titleVendorPrimaryContact', {
          count: primaryContactItems.filter((item) => item.value != null && !item.hidden).length,
        })}
        items={primaryContactItems}
        filterEmptyLines
      />

      <Typography level={Level.TITLE_LG}>
        {t('titleVendorPurchasingAttributes', {
          lengthValue: filteredPurchasingAttributes.length || DEFAULT_PLACEHOLDER,
        })}
      </Typography>

      {filteredPurchasingAttributes.map((attribute, index) => (
        <InfoCard
          key={attribute.shippingLocation ?? index}
          title={t('titleVendorShippingLocation', {
            shippingLocation: attribute.shippingLocation,
          })}
          titleLevel={Level.TITLE_SM}
          items={getPurchasingAttributeItems(attribute)}
          filterEmptyLines
        />
      ))}
    </Stack>
  );
};
